import { wmsApi } from '../../../config/apiClient';
import { ACCOUNTING_API } from '../../../config/api';

export interface StatusCount {
  status: string;
  count: number;
}

export interface ChannelCount {
  channel: string;
  count: number;
}

export interface DashboardSummary {
  pendingOrders: number;
  pickingOrders: number;
  packingOrders: number;
  shippedOrders: number;
  activeWaves: number;
  activePickers: number;
  avgPickingTime: number;
  inventoryAccuracy: number;
  pendingChange: number;
  shippedChange: number;
  totalPickableLocations?: number;
  occupiedLocations?: number;
  emptyLocations?: number;
  occupancyRatePercent?: number;
  awaitingCargo?: number;
  cancelledToday?: number;
  pipeline?: StatusCount[];
  channels?: ChannelCount[];
}

export interface HourlyData {
  hour: number;
  label?: string;
  ordersReceived: number;
  ordersPicked: number;
  ordersPacked: number;
}

export interface ThroughputData {
  totalPicked: number;
  totalPacked: number;
  totalShipped: number;
}

export interface ActiveWave {
  id: string;
  waveNumber: string;
  waveType: string;
  assignedPickerName?: string;
  totalItems: number;
  pickedItems: number;
  progress: number;
  startedAt?: string;
}

export interface ActivityFeedItem {
  timestamp: string;
  type: string;
  description: string;
  personnelName: string;
}

export interface OverviewReport {
  totalOrders: number;
  fulfilledOrders: number;
  cancelledOrders: number;
  pendingOrders: number;
  fulfillmentRate: number;
  cancellationRate: number;
  avgFulfillmentHours: number;
  dailyData: DailyCount[];
}

export interface DailyCount {
  date: string;
  received: number;
  fulfilled: number;
  cancelled: number;
}

export interface PickerPerformance {
  pickerName: string;
  waveCount: number;
  itemCount: number;
  avgDuration: number;
  accuracy: number;
  efficiency: number;
}

/** @deprecated Artık kullanılmıyor — yeni API PackingOrderReport döndürüyor */
export interface PackerPerformance {
  packerName: string;
  packageCount: number;
  avgDuration: number;
  errorCount: number;
  efficiency: number;
}

export interface PackingOrderReport {
  date: string;
  orderNumber: string;
  orderType?: string;
  itemCount: number;
  shippingMethod?: string;
  status: string;
}

export interface ReceivingReportItem {
  date: string;
  sessionNumber: string;
  productTypes: number;
  totalItems: number;
  placedItems: number;
  placedPercent: number;
}

export interface CountingReportItem {
  taskNumber: string;
  date: string;
  totalLocations: number;
  discrepancyCount: number;
  accuracyPercent: number;
}

export interface ShortPickReportItem {
  date: string;
  productName: string;
  locationCode: string;
  pickerName: string;
  description?: string;
}

interface DateRange {
  dateFrom?: string;
  dateTo?: string;
}

const unwrap = <T>(resp: any, fallback: T): T => {
  const raw = resp?.data?.data ?? resp?.data?.Data ?? resp?.data;
  return (raw === undefined || raw === null) ? fallback : raw as T;
};

const mapHourly = (raw: any): HourlyData[] => {
  const list = Array.isArray(raw) ? raw : (raw?.data ?? raw?.Data ?? []);
  if (!Array.isArray(list)) return [];
  return list.map((d: any) => ({
    hour: Number(d?.hour ?? d?.Hour ?? 0),
    label: d?.label ?? d?.Label ?? undefined,
    ordersReceived: Number(d?.ordersReceived ?? d?.OrdersReceived ?? 0),
    ordersPicked: Number(d?.ordersPicked ?? d?.OrdersPicked ?? 0),
    ordersPacked: Number(d?.ordersPacked ?? d?.OrdersPacked ?? 0),
  }));
};

const mapSummary = (raw: any): DashboardSummary | null => {
  if (!raw || typeof raw !== 'object') return null;
  return {
    pendingOrders: Number(raw.pendingOrders ?? raw.PendingOrders ?? 0),
    pickingOrders: Number(raw.pickingOrders ?? raw.PickingOrders ?? 0),
    packingOrders: Number(raw.packingOrders ?? raw.PackingOrders ?? 0),
    shippedOrders: Number(raw.shippedOrders ?? raw.ShippedOrders ?? 0),
    activeWaves: Number(raw.activeWaves ?? raw.ActiveWaves ?? 0),
    activePickers: Number(raw.activePickers ?? raw.ActivePickers ?? 0),
    avgPickingTime: Number(raw.avgPickingTime ?? raw.AvgPickingTime ?? 0),
    inventoryAccuracy: Number(raw.inventoryAccuracy ?? raw.InventoryAccuracy ?? 100),
    pendingChange: Number(raw.pendingChange ?? raw.PendingChange ?? 0),
    shippedChange: Number(raw.shippedChange ?? raw.ShippedChange ?? 0),
    totalPickableLocations: Number(raw.totalPickableLocations ?? raw.TotalPickableLocations ?? 0),
    occupiedLocations: Number(raw.occupiedLocations ?? raw.OccupiedLocations ?? 0),
    emptyLocations: Number(raw.emptyLocations ?? raw.EmptyLocations ?? 0),
    occupancyRatePercent: Number(raw.occupancyRatePercent ?? raw.OccupancyRatePercent ?? 0),
    awaitingCargo: Number(raw.awaitingCargo ?? raw.AwaitingCargo ?? 0),
    cancelledToday: Number(raw.cancelledToday ?? raw.CancelledToday ?? 0),
    pipeline: (raw.pipeline ?? raw.Pipeline ?? []).map((p: any) => ({
      status: String(p?.status ?? p?.Status ?? ''),
      count: Number(p?.count ?? p?.Count ?? 0),
    })),
    channels: (raw.channels ?? raw.Channels ?? []).map((c: any) => ({
      channel: String(c?.channel ?? c?.Channel ?? ''),
      count: Number(c?.count ?? c?.Count ?? 0),
    })),
  };
};

const dashboardService = {
  getSummary: async (warehouseId: string): Promise<DashboardSummary | null> => {
    const resp = await wmsApi.get(ACCOUNTING_API.WMS_DASHBOARD_SUMMARY(warehouseId));
    return mapSummary(unwrap(resp, null));
  },

  getHourly: async (warehouseId: string, period: string = 'today'): Promise<HourlyData[]> => {
    const resp = await wmsApi.get(ACCOUNTING_API.WMS_DASHBOARD_HOURLY(warehouseId), { params: { period } });
    return mapHourly(unwrap(resp, []));
  },

  getThroughput: async (warehouseId: string, period: string = 'today'): Promise<ThroughputData> => {
    const resp = await wmsApi.get(ACCOUNTING_API.WMS_DASHBOARD_THROUGHPUT(warehouseId), { params: { period } });
    const raw = unwrap<any>(resp, {});
    return {
      totalPicked: Number(raw?.totalPicked ?? raw?.TotalPicked ?? 0),
      totalPacked: Number(raw?.totalPacked ?? raw?.TotalPacked ?? 0),
      totalShipped: Number(raw?.totalShipped ?? raw?.TotalShipped ?? 0),
    };
  },

  getActiveWaves: async (warehouseId: string): Promise<ActiveWave[]> => {
    const resp = await wmsApi.get(ACCOUNTING_API.WMS_DASHBOARD_ACTIVE_WAVES(warehouseId));
    return resp.data?.data ?? [];
  },

  getActivityFeed: async (warehouseId: string): Promise<ActivityFeedItem[]> => {
    const resp = await wmsApi.get(ACCOUNTING_API.WMS_DASHBOARD_FEED(warehouseId));
    return resp.data?.data ?? [];
  },

  getOverviewReport: async (warehouseId: string, range?: DateRange): Promise<OverviewReport> => {
    const resp = await wmsApi.get(ACCOUNTING_API.WMS_REPORT_OVERVIEW(warehouseId), { params: range });
    return resp.data?.data;
  },

  getPickingReport: async (warehouseId: string, range?: DateRange): Promise<PickerPerformance[]> => {
    const resp = await wmsApi.get(ACCOUNTING_API.WMS_REPORT_PICKING(warehouseId), { params: range });
    return resp.data?.data ?? [];
  },

  getPackingReport: async (warehouseId: string, range?: DateRange): Promise<PackingOrderReport[]> => {
    const resp = await wmsApi.get(ACCOUNTING_API.WMS_REPORT_PACKING(warehouseId), { params: range });
    return resp.data?.data ?? [];
  },

  getReceivingReport: async (warehouseId: string, range?: DateRange): Promise<ReceivingReportItem[]> => {
    const resp = await wmsApi.get(ACCOUNTING_API.WMS_REPORT_RECEIVING(warehouseId), { params: range });
    return resp.data?.data ?? [];
  },

  getCountingReport: async (warehouseId: string, range?: DateRange): Promise<CountingReportItem[]> => {
    const resp = await wmsApi.get(ACCOUNTING_API.WMS_REPORT_COUNTING(warehouseId), { params: range });
    return resp.data?.data ?? [];
  },

  getShortPicksReport: async (warehouseId: string, range?: DateRange): Promise<ShortPickReportItem[]> => {
    const resp = await wmsApi.get(ACCOUNTING_API.WMS_REPORT_SHORT_PICKS(warehouseId), { params: range });
    return resp.data?.data ?? [];
  },
};

export default dashboardService;
